import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const OPERATORS = ["gt", "gte", "lt", "lte", "eq", "contains"];
const SEVERITIES = ["low", "medium", "high"];
const ACTIONS = ["flag", "block", "require_receipt", "require_approval"];

const Label = ({ children }) => (
  <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[#6b6b6b]">{children}</div>
);

export const PolicyEditDialog = ({ policy, open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && policy) {
      setForm({
        name: policy.name || "",
        description: policy.description || "",
        category: policy.category || "Expense",
        severity: policy.severity || "medium",
        action: policy.action || "flag",
        conditions: (policy.conditions || []).map((c) => ({ ...c, value: String(c.value ?? "") })),
        exceptions: [...(policy.exceptions || [])],
      });
    }
  }, [open, policy]);

  if (!form) return null;

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setCond = (i, key, value) =>
    setForm((f) => ({ ...f, conditions: f.conditions.map((c, ci) => (ci === i ? { ...c, [key]: value } : c)) }));
  const setExc = (i, value) =>
    setForm((f) => ({ ...f, exceptions: f.exceptions.map((x, xi) => (xi === i ? value : x)) }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("The policy needs a name"); return; }
    setSaving(true);
    try {
      const conditions = form.conditions
        .filter((c) => c.field && c.value !== "")
        .map((c) => ({ ...c, value: /^\d+(\.\d+)?$/.test(c.value) ? Number(c.value) : c.value }));
      await api.patch(`/policies/${policy.id}`, {
        name: form.name.trim(),
        description: form.description,
        category: form.category,
        severity: form.severity,
        action: form.action,
        conditions,
        exceptions: form.exceptions.filter((x) => x.trim()),
      });
      toast.success(`Policy updated: ${form.name.trim()}`);
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error("Could not save the policy");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[86vh] overflow-y-auto border-[#242424] bg-[#111111] text-white sm:max-w-[520px]" data-testid="policy-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-semibold tracking-tight">Edit policy</DialogTitle>
          <DialogDescription className="text-[12px] text-[#A3A3A3]">
            Changes apply to all future expense checks per your policy controls.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="policy-edit-name"
              className="h-8 border-[#2a2a2a] bg-[#161616] text-[12.5px] text-white" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="policy-edit-description"
              className="min-h-[64px] resize-none border-[#2a2a2a] bg-[#161616] text-[12.5px] text-white" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Category</Label>
              <Input value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="policy-edit-category"
                className="h-8 border-[#2a2a2a] bg-[#161616] text-[12px] text-white" />
            </div>
            <div>
              <Label>Severity</Label>
              <Select value={form.severity} onValueChange={(v) => set("severity", v)}>
                <SelectTrigger className="h-8 border-[#2a2a2a] bg-[#161616] text-[12px]" data-testid="policy-edit-severity"><SelectValue /></SelectTrigger>
                <SelectContent className="border-[#222] bg-[#161616] text-white">
                  {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Action</Label>
              <Select value={form.action} onValueChange={(v) => set("action", v)}>
                <SelectTrigger className="h-8 border-[#2a2a2a] bg-[#161616] text-[12px]" data-testid="policy-edit-action"><SelectValue /></SelectTrigger>
                <SelectContent className="border-[#222] bg-[#161616] text-white">
                  {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Conditions (all must match)</Label>
            <div className="space-y-1.5">
              {form.conditions.map((c, i) => (
                <div key={i} className="flex items-center gap-1.5" data-testid={`policy-edit-condition-${i}`}>
                  <Input value={c.field} onChange={(e) => setCond(i, "field", e.target.value)} placeholder="field (e.g. amount)"
                    className="h-8 flex-1 border-[#2a2a2a] bg-[#161616] text-[12px] text-white placeholder:text-[#555]" />
                  <Select value={c.operator} onValueChange={(v) => setCond(i, "operator", v)}>
                    <SelectTrigger className="h-8 w-24 border-[#2a2a2a] bg-[#161616] text-[12px]"><SelectValue /></SelectTrigger>
                    <SelectContent className="border-[#222] bg-[#161616] text-white">
                      {OPERATORS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input value={c.value} onChange={(e) => setCond(i, "value", e.target.value)} placeholder="value"
                    className="h-8 w-24 border-[#2a2a2a] bg-[#161616] text-[12px] tabular-nums text-white placeholder:text-[#555]" />
                  <button onClick={() => set("conditions", form.conditions.filter((_, ci) => ci !== i))}
                    data-testid={`policy-edit-condition-remove-${i}`}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#6b6b6b] hover:bg-[#1c1c1c] hover:text-[#F87171]">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
              <button onClick={() => set("conditions", [...form.conditions, { field: "amount", operator: "gt", value: "" }])}
                data-testid="policy-edit-add-condition"
                className="flex items-center gap-1.5 rounded-lg border border-dashed border-[#2a2a2a] px-3 py-1.5 text-[11.5px] text-[#A3A3A3] hover:border-[#3a3a3a] hover:text-white">
                <Plus size={12} /> Add condition
              </button>
            </div>
          </div>

          <div>
            <Label>Exceptions</Label>
            <div className="space-y-1.5">
              {form.exceptions.map((x, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <Input value={x} onChange={(e) => setExc(i, e.target.value)} data-testid={`policy-edit-exception-${i}`}
                    className="h-8 flex-1 border-[#2a2a2a] bg-[#161616] text-[12px] text-white" />
                  <button onClick={() => set("exceptions", form.exceptions.filter((_, xi) => xi !== i))}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#6b6b6b] hover:bg-[#1c1c1c] hover:text-[#F87171]">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
              <button onClick={() => set("exceptions", [...form.exceptions, ""])} data-testid="policy-edit-add-exception"
                className="flex items-center gap-1.5 rounded-lg border border-dashed border-[#2a2a2a] px-3 py-1.5 text-[11.5px] text-[#A3A3A3] hover:border-[#3a3a3a] hover:text-white">
                <Plus size={12} /> Add exception
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)}
            className="h-8 border-[#2a2a2a] bg-transparent px-3 text-[12px] text-[#A3A3A3] hover:bg-[#1c1c1c] hover:text-white">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="policy-edit-save"
            className="h-8 btn-gradient px-4 text-[12.5px] font-medium text-white disabled:opacity-40">
            {saving ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : null} Save changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
